// ============================================================================
// 遠距離(地形のみ)の解析グリッド生成
// ----------------------------------------------------------------------------
// 近距離は道路沿いのサンプリング(roadPoints.ts)で精密に判定するため、
// このグリッドは「大まかな全体像」用の遠距離帯(rNear〜rFar)のみを埋める。
// 「ヒートマップでぼかす」のではなく、ハードエッジの正方形セルで塗り分ける。
// ============================================================================

import { degOffset, distanceMeters } from "./geo";
import type { BuildingIndex } from "./buildingIndex";
import type { LngLat } from "./types";

export interface GridCell {
  lng: number;
  lat: number;
  sizeM: number;
}

/** 中心から半径rNear〜rFar(m)を間隔cellSizeで格子状に埋める。 */
export function generateGrid(
  center: LngLat,
  rNear: number,
  rFar: number,
  cellSize = 250
): GridCell[] {
  const cells: GridCell[] = [];
  if (rFar <= rNear) return cells;
  const { dLng, dLat } = degOffset(cellSize, center[1]);
  const steps = Math.ceil(rFar / cellSize);
  for (let iy = -steps; iy <= steps; iy++) {
    for (let ix = -steps; ix <= steps; ix++) {
      const lng = center[0] + ix * dLng;
      const lat = center[1] + iy * dLat;
      const d = distanceMeters(center, [lng, lat]);
      if (d >= rNear && d < rFar) {
        cells.push({ lng, lat, sizeM: cellSize });
      }
    }
  }
  return cells;
}

/**
 * 建物フットプリントの中に入っていないセルのindex一覧を返す（建物レイヤ側で
 * 描画するため、そちらは除外する）。値ではなくindexで返すのは、呼び出し側が
 * 元のcells配列との対応を安価に保てるようにするため。
 */
export function filterCellIndices(cells: GridCell[], index: BuildingIndex): number[] {
  const out: number[] = [];
  for (let i = 0; i < cells.length; i++) {
    if (!index.findAt([cells[i].lng, cells[i].lat])) out.push(i);
  }
  return out;
}

/** セル中心とサイズから正方形リング（閉じたポリゴン頂点列）を作る。 */
export function cellToRing(cell: GridCell): LngLat[] {
  const { dLng, dLat } = degOffset(cell.sizeM / 2, cell.lat);
  const { lng, lat } = cell;
  return [
    [lng - dLng, lat - dLat],
    [lng + dLng, lat - dLat],
    [lng + dLng, lat + dLat],
    [lng - dLng, lat + dLat],
    [lng - dLng, lat - dLat],
  ];
}
